import { FormikProvider, useFormik } from "formik";
import { useEffect, useState } from "react";
import DropdownField, {
  DropdownOptions,
} from "../../../../components/FormFields/DropdownField";
import {
  getJuryDropdownByProgramId,
  getRootProgramOptionsWithJury,
  getRoundDropdownByJuryId,
  getAllVotesReport, // <-- ADD THIS fetcher hitting your /all-votes-report/{roundId}/{juryId}
} from "../../Program/manageProgram";
import styled from "styled-components";
import * as Yup from "yup";
import Button from "../../../../components/Button/Button";
import JudgingResultsTable from "./JudgingResultsTable";
import config from "../../../../config";
import AllVotesResultsTable from "./AllVotesResultsTable";

const AllVotes = () => {
  const [rootProgramIDs, setRootProgramIDs] = useState<DropdownOptions[]>([]);
  const [juryDropdownOptions, setJuryDropdownOptions] = useState<
    DropdownOptions[]
  >([]);
  const [roundDropdownOptions, setRoundDropdownOptions] = useState<
    DropdownOptions[]
  >([]);
  const [selectedProgramId, setSelectedProgramId] = useState<
    number | undefined
  >();
  const [selectedProgramName, setSelectedProgramName] = useState<string>();
  const [selectedJuryId, setSelectedJuryId] = useState<number | undefined>();
  const [selectedRoundId, setSelectedRoundId] = useState<number | undefined>();
  const [reportLoading, setReportLoading] = useState(false);
  const [allVotesReport, setAllVotesReport] = useState<AllVotesReport[]>([]);
  const [selectedRoundName, setSelectedRoundName] = useState<
    string | undefined
  >();

  const LoadingSpinner = styled.img`
    width: 100px;
  `;

  const spinnerSrc = config.assets.loading.primary;

  // Fetch root program options on mount
  useEffect(() => {
    getRootProgramOptionsWithJury().then((res) => {
      setRootProgramIDs(res.data);
    });
  }, []);

  useEffect(() => {
    if (selectedProgramId) {
      getJuryDropdownByProgramId(selectedProgramId).then((data) => {
        setJuryDropdownOptions(
          data.length > 0
            ? data
            : [{ value: "", label: "No Juries Exist for Program" }]
        );
      });
    }
  }, [selectedProgramId]);

  useEffect(() => {
    if (selectedJuryId) {
      getRoundDropdownByJuryId(selectedJuryId).then((data) => {
        setRoundDropdownOptions(
          data.length > 0
            ? data
            : [{ value: "", label: "No Rounds Exist for Jury" }]
        );
      });
    }
  }, [selectedJuryId]);

  useEffect(() => {
    setSelectedJuryId(undefined);
    setSelectedRoundId(undefined);
    setJuryDropdownOptions([]);
    setRoundDropdownOptions([]);
    setAllVotesReport([]);
  }, [selectedProgramId]);

  useEffect(() => {
    setSelectedRoundId(undefined);
    setRoundDropdownOptions([]);
    setAllVotesReport([]);
  }, [selectedJuryId]);

  const generateInformation = async () => {
    if (selectedProgramId && selectedJuryId && selectedRoundId) {
      setReportLoading(true);
      try {
        const report: AllVotesReport[] = await getAllVotesReport(
          selectedRoundId,
          selectedJuryId
        );
        setAllVotesReport(report);
      } catch (error) {
        console.error("Failed to fetch All Votes Report: ", error);
      }
      setReportLoading(false);
    }
  };

  const formikProps = useFormik({
    initialValues: { rootProgramIDs },
    enableReinitialize: true,
    onSubmit: async () => {},
    validationSchema: Yup.object({}),
    validateOnBlur: false,
    validateOnChange: false,
  });

  const reportName = `${selectedProgramName}_Jury ${selectedJuryId}_Round ${selectedRoundName}_AllVotes`;

  return (
    <div>
      <FormikProvider value={formikProps}>
        <div className="flex items-center space-x-4 pb-5">
          <DropdownField
            name="rootProgram"
            options={rootProgramIDs}
            value={selectedProgramId}
            placeholder="Select a Program"
            onChange={(value) => {
              const selectedValue = Number(value.target.value);
              const selectedOption = rootProgramIDs.find(
                (option) => option.value === selectedValue
              );
              setSelectedProgramId(selectedValue);
              setSelectedProgramName(String(selectedOption?.label || ""));
            }}
          />
          {juryDropdownOptions.length > 0 && (
            <DropdownField
              name="jury"
              options={juryDropdownOptions}
              placeholder="Select a Jury"
              disabled={!selectedProgramId}
              onChange={(value) => {
                setSelectedJuryId(Number(value.target.value));
              }}
            />
          )}
          {roundDropdownOptions.length > 0 && (
            <>
              <DropdownField
                name="round"
                options={roundDropdownOptions}
                placeholder="Round"
                disabled={!selectedJuryId}
                onChange={(value) => {
                  const selectedValue = Number(value.target.value);
                  const selectedOption = roundDropdownOptions.find(
                    (option) => option.value === selectedValue
                  );
                  setSelectedRoundId(selectedValue);
                  setSelectedRoundName(String(selectedOption?.label || ""));
                }}
              />
              <Button
                onClick={generateInformation}
                disabled={
                  !selectedProgramId ||
                  !selectedJuryId ||
                  !selectedRoundId ||
                  reportLoading
                }
              >
                Generate
              </Button>
            </>
          )}
        </div>
        {reportLoading ? (
          <div className="pt-10 flex flex-col items-center justify-center">
            <div>Generating Report...</div>
            <LoadingSpinner src={spinnerSrc} />
          </div>
        ) : (
          allVotesReport.length > 0 && (
            <AllVotesResultsTable
              reportName={reportName}
              data={allVotesReport}
              disableExport={false}
              showTopScroll={true}
            />
          )
        )}
      </FormikProvider>
    </div>
  );
};

export interface AllVotesReport {
  voteId: number;
  entryId: number;
  entryTitle: string;
  brand: string;
  entrantCompany: string;
  authorCompany: string;
  jurorId: number;
  jurorName: string;
  jurorCompany: string;
  voteOptionId: number | null;
  voteOption: string;
  voteValue: number | null;
  voteCastDate: string; // ISO string
  batch: string;
  entrySet: string;
  juryId: number;
  jury: string;
  round: string;
  roundTypeId: number;
  totalScore: number;
}

export default AllVotes;
